import { isPlainObject, isString, objectHasKeys, pluralize } from '@art-suite/art-core-ts';
import { zodLessParse } from '@kaiizen/zod-less';

import { Scope } from './OptimizedValueExpressions';
import { getOptimizedValueExpressions } from './ScanRootOptimized';

import { ScanPartContext } from './ScanRoot';
import {
  getNormalizedScanTemplateRoot,
  ScanTemplateGroup,
  ScanTemplateLineItem,
  ScanTemplateOption,
  ScanTemplatePart,
  ScanTemplateRoot,
  ScanTemplateRootSchema,
  ScanTemplateTask,
} from './ScanTemplateRoot';

export type ScanTemplateValidationDetails = ScanPartContext & {
  message: string;
};

export class ScanTemplateValidationError extends Error {
  details: ScanTemplateValidationDetails[];
  constructor(message: string, details: ScanTemplateValidationDetails[]) {
    super(message);
    this.name = 'ScanTemplateValidationError';
    this.details = details;
  }
}

const addIssue = (issues: ScanTemplateValidationDetails[], issue: ScanTemplateValidationDetails) => {
  issues.push(issue);
};

export const addErrorToIssues = (issues: ScanTemplateValidationDetails[], error: any, context?: ScanPartContext) => {
  issues.push({ message: error instanceof Error ? error.message : String(error), ...context });
};

// ************************************************************************
// validate all - especially equations
// ************************************************************************

const validateResolvedValues = (
  template: ScanTemplatePart,
  scope: Scope,
  context: string | undefined,
  issues: ScanTemplateValidationDetails[],
  groupId?: string,
  taskId?: string,
  optionId?: string,
  lineItemId?: string,
) => {
  try {
    const optimizedExpressions = getOptimizedValueExpressions(template, scope);
    optimizedExpressions.validateAllDependenciesDefined(context);
    return optimizedExpressions.resolved;
  } catch (error: any) {
    addErrorToIssues(issues, error, { groupId, taskId, optionId, lineItemId });
  }
};

export const validateScanTemplateLineItem = (
  lineItem: ScanTemplateLineItem,
  scope: Scope,
  context?: string,
  issues: ScanTemplateValidationDetails[] = [],
  groupId?: string,
  taskId?: string,
  optionId?: string,
) => {
  context = `${context ? context + ', ' : ''}Line Item: ${lineItem.name} (${lineItem.id})`;
  try {
    validateResolvedValues(lineItem, scope, context, issues, groupId, taskId, optionId, lineItem.id);
  } catch (error: any) {
    issues.push({ groupId, taskId, optionId, lineItemId: lineItem.id, message: error instanceof Error ? error.message : String(error) });
  }
};

export const validateScanTemplateOption = (
  option: ScanTemplateOption,
  scope: Scope,
  context?: string,
  issues: ScanTemplateValidationDetails[] = [],
  groupId?: string,
  taskId?: string,
) => {
  context = `${context ? context + ', ' : ''}Option: ${option.name} (${option.id})`;
  const optionScope = validateResolvedValues(option, scope, context, issues, groupId, taskId, option.id);
  if (optionScope) {
    for (const lineItem of option.lineItemTemplates ?? []) {
      validateScanTemplateLineItem(lineItem, optionScope, context, issues, groupId, taskId, option.id);
    }
  }
};

export const validateScanTemplateTask = (
  task: ScanTemplateTask,
  scope: Scope,
  context?: string,
  issues: ScanTemplateValidationDetails[] = [],
  groupId?: string,
) => {
  context = `${context ? context + ', ' : ''}Task: ${task.name} (${task.id})`;
  const taskScope = validateResolvedValues(task, scope, context, issues, groupId, task.id);
  if (taskScope) {
    for (const option of task.optionTemplates ?? []) {
      validateScanTemplateOption(option, taskScope, context, issues, groupId, task.id);
    }
  }
};

/**
 * Validate a group template's resolved values and references.
 *
 * In permissive mode, this will de-duplicate and prune `taskTemplateIds` to only
 * include existing tasks before validation. In strict mode, no mutations are made.
 *
 * @param group Group template to validate.
 * @param rootTemplate Root template used for task lookups by id.
 * @param scope Parent scope to resolve calculated values.
 * @param context Optional validation context string for error messages.
 * @param issues Accumulator for validation issues.
 * @param groupId Optional group id override for issue context.
 * @param permissive If true, tries to fix/normalize `taskTemplateIds`. Default: false (strict mode).
 */
export const validateScanTemplateGroup = (
  group: ScanTemplateGroup,
  rootTemplate: ScanTemplateRoot,
  scope: Scope,
  context?: string,
  issues: ScanTemplateValidationDetails[] = [],
  groupId?: string,
  permissive: boolean = false,
) => {
  context = `${context ? context + ', ' : ''}Group: ${group.name} (${group.id})`;

  // If we aren't running in strict mode, we can attempt to clean up the taskTemplateIds array.
  // Make sure there are no duplicates, and that all referenced tasks actually exist.
  // We can do this regardless of whether or not we have a Group scope.
  if (permissive) {
    if (group.taskTemplateIds) {
      group.taskTemplateIds = [...new Set(group.taskTemplateIds)].sort();
      group.taskTemplateIds = group.taskTemplateIds.filter((taskId: string) => {
        const task = rootTemplate.taskTemplatesById?.[taskId];
        return !!task;
      });
    }
  }

  const groupScope = validateResolvedValues(group, scope, context, issues, groupId);
  if (groupScope) {
    for (const task of group.taskTemplates ?? []) {
      validateScanTemplateTask(task, groupScope, context, issues, group.id);
    }
    if (group.taskTemplateIds) {
      for (const taskId of group.taskTemplateIds) {
        const task = rootTemplate.taskTemplatesById?.[taskId];
        if (!task) {
          addIssue(issues, { message: `Task template not found for taskId: ${taskId} in group: ${group.name} ${group.id}`, groupId: group.id, taskId: taskId });
        } else {
          validateScanTemplateTask(task, groupScope, context, issues, group.id);
        }
      }
    }
  }
};

const validateTaskTemplatesById = (rootTemplate: ScanTemplateRoot, issues: ScanTemplateValidationDetails[]): void => {
  if (objectHasKeys(rootTemplate.taskTemplatesById)) {
    const taskTemplateIds = Object.keys(rootTemplate.taskTemplatesById);
    const taskTemplateIdsThatDoNotMatchIds = taskTemplateIds.filter(id => id !== rootTemplate.taskTemplatesById?.[id].id)
      .map(id => `${id} -> ${rootTemplate.taskTemplatesById?.[id].id}`);

    taskTemplateIdsThatDoNotMatchIds.forEach(id => {
      addIssue(issues, { message: `Task template by ID Key does not match the .id of the object. Key/ID pair: ${id}`, taskId: id });
    });
  }
};

/**
 * Validate all IDs and calculation variable references are valid.
 * Throws an error if the template is invalid.
 * Will try to fix certain common issues if it can, unless `strict` is true.
 * @param rootTemplate The scan template root to validate.
 * @param permissive If true, attempts to fix any issues but only reports them. Default: false (strict mode).
 */
export const validateScanTemplateData = (rootTemplate: ScanTemplateRoot, permissive: boolean = false): void => {
  const issues: ScanTemplateValidationDetails[] = [];
  try {
    validateTaskTemplatesById(rootTemplate, issues);
    const rootScope = validateResolvedValues(rootTemplate, {}, undefined, issues);
    if (rootScope) {
      for (const group of rootTemplate.groupTemplates ?? []) {
        validateScanTemplateGroup(group, rootTemplate, rootScope, undefined, issues, undefined, permissive);
      }
    }
  } catch (error: any) {
    addErrorToIssues(issues, error);
  }
  if (issues.length > 0) {
    throw new ScanTemplateValidationError(`Template Validation - ${pluralize('error', issues.length)}:\n` + issues.map(i => i.message).join('\n'), issues);
  }
};

/**
 * Parse a scan template root
 * Note: use validateTemplate to both parse AND validate internal data consistency
 * @param scanTemplateRoot object or JSON string
 * @returns structurally valid, normalized,parsed scan template root, but may have cross-reference inconsistencies
 */
export const parseScanTemplateRoot = (scanTemplateRoot: any): ScanTemplateRoot => {
  if (isString(scanTemplateRoot)) scanTemplateRoot = JSON.parse(scanTemplateRoot);
  if (!isPlainObject(scanTemplateRoot)) throw new Error('scanTemplateRoot must be an object or a JSON string parsing to an object');
  const validScanTemplateRoot = zodLessParse(ScanTemplateRootSchema, scanTemplateRoot);
  return getNormalizedScanTemplateRoot(validScanTemplateRoot);
};

/**
 * Validate a template's structure and data consistency (all internal cross-references are valid)
 * Parses the template, which involves making a new copy and possibly omitting some fields
 * Throws an error if the template is invalid
 * @param scanTemplateRoot object or JSON string
 * @returns new, parsed & validated template (possibly with some invalid fields omitted rather than throwing an error)
 */
export const validateTemplate = (scanTemplateRoot: any): ScanTemplateRoot => {
  const validTemplate = parseScanTemplateRoot(scanTemplateRoot);
  validateScanTemplateData(validTemplate);
  return validTemplate;
};
